import { type ComponentType, lazy } from 'react';

import type { RecoveryOptions } from '../survey-tool/utils/dynamic-import-recovery';
import { loadWithReload as loadWithRecovery } from '../survey-tool/utils/dynamic-import-recovery';
import { describeModule, detectDeploy, pageContext, suspectAssetEntries } from './chunk-load-diagnostics';
import { logWarnWithContext } from './log';

// The stale-chunk recovery itself (module validation, the one-shot reload guard, and the
// dynamic-import signature list) lives in the survey-tool submodule, which is compiled as
// first-party source here. This file supplies only the pieces of policy that are
// specific to THIS app and deliberately not owned by the shared library: how a
// guard-exhausted failure is reported, and where the user is sent.
//
// It used to be a full copy of that logic. The duplication was a liability: the same
// correctness fix had to be applied twice in two repos, and the reload guard's
// sessionStorage key was a contract held together by a matching string literal on each
// side — a rename on either side would have caused a double reload. One implementation
// removes both problems.

function buildErrorUrl(title: string, desc: string): string {
  return `${window.location.origin}/error?title=${encodeURIComponent(title)}&desc=${encodeURIComponent(desc)}`;
}

// A persistent lazy-load failure (reload guard already armed). Redirect to the /error
// page instead of leaving a broken route. Loop guard: if the /error page's OWN chunk is
// what failed (we're already on /error), throw instead so the ErrorBoundary's inline,
// dependency-light fallback renders rather than bouncing back to /error forever.
//
// The copy depends on whether a deploy actually happened. "Update in progress, try again
// in 5 minutes" is only honest when /index.html now points at a different entry script
// than the one this page booted from. When the entry is unchanged the failure is not
// deploy skew (network, blocked or corrupted asset), so we say so and suggest a
// connectivity check. If the check cannot decide ('unknown') we keep the update copy. The
// verdict is logged as its own warn event so the two populations can be told apart.
async function redirectToErrorOrThrow<T extends ComponentType<any>>(thrown: unknown): Promise<{ default: T }> {
  if (window.location.pathname === '/error') {
    throw thrown;
  }
  const deploy = await detectDeploy();
  try {
    logWarnWithContext('lazyWithReload: guard-exhausted redirect', { deploy });
  } catch {
    // Telemetry failure must never block the redirect.
  }
  window.location.href =
    deploy === 'same-build'
      ? buildErrorUrl("We couldn't load this page", 'Check your internet connection, then refresh the page.')
      : buildErrorUrl('Update in progress', 'Please refresh the page again in 5 minutes.');
  // Suspend forever while the browser navigates away, so React never renders a broken module.
  return await new Promise<{ default: T }>(() => {});
}

// Exported for unit testing.
export async function loadWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  // Capture what the factory resolved to, so a "resolved without a renderable default"
  // report can say what the module actually looked like.
  let resolved: unknown;
  const tracked = async () => {
    const m = await factory();
    resolved = m;
    return m;
  };
  // Report via logWarnWithContext rather than the library's default console.error: a
  // persistent chunk failure is not an app bug, and the structured context (module shape,
  // suspicious asset requests, page state) is what lets us tell causes apart.
  const recoveryOptions: RecoveryOptions = {
    report: (message) =>
      logWarnWithContext(message, {
        ...describeModule(resolved),
        assets: suspectAssetEntries(),
        ...pageContext(),
      }),
    onGuardExhausted: redirectToErrorOrThrow,
  };
  return await loadWithRecovery(tracked, recoveryOptions);
}

// Drop-in replacement for React.lazy for first-party route chunks.
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() => loadWithReload(factory));
}
